import React, { useEffect, useState, useCallback, useMemo, useRef } from 'react';
import OverlayTrigger from 'react-bootstrap/OverlayTrigger';
import Popover from 'react-bootstrap/Popover';
import { FormGroup } from 'react-bootstrap';
import { MultiSelect } from 'react-multi-select-component';
import { useSelector, useDispatch } from 'react-redux';
import { isEmpty, set } from 'lodash';
import { useTranslation } from 'react-i18next';
import * as todoAction from './store/Submit.action';
import * as commonAction from '../TodoCommon.action';
import { createOptions, checkArray, Page_Size, months, getYears } from '../../../../Components/helper';
import { getSupplierBrandsAction } from '../Costing/store/Costing.action';
import * as systemMasterAction from '../../../SystemMasters/Store/SystemMasters.action';
import { CurrentYearItemRenderer } from '../../../../Components/Utility/CurrentYearDropdown';

function SubmitFilter() {
  const dispatch = useDispatch();
  const { t } = useTranslation('common');
  const { t: tReports } = useTranslation('todo-reports');

  const reducers = useSelector((redState) => redState);
  const {
    UserDetails: {
      userInfo: { userId },
      isSupplierLoggedIn,
      isCompanyUserLoggedIn,
    },
    submitTodoListReducer: {
      submitFilterValues: {
        selected_Season,
        selected_Brand,
        selected_SubBrand,
        selected_Supplier,
        selected_Category1,
        selected_Category2,
        selected_Category3,
        selected_Category4,
        sortOrder,
        sortPropertyName,
        selected_CreatedBy,
        selected_ModifiedBy,
        selected_Send_To,
        selected_DownloadBy,
        selected_SendBy,
        selected_Submitted,
        selected_Month,
        selected_Year,
        selected_ProductType,
        selectedLicensor,
        selectedLicense,
        selectedLikeness,
      },
    },
    SystemMastersDetails: { multiSubBrandList, pcTypeList, licensorList, licenseList, likenessList },
  } = reducers;
  const isAnySupplierSelected = selected_Supplier && selected_Supplier.length !== 0;
  const [state, setState] = useState({
    seasonOptions: [],
    toOptions: [],
    downloadOptions: [],
    yearOptions: createOptions(getYears(), 'name', '_id'),
    monthOptions: createOptions(months, 'name', '_id'),
    brandOptions: [],
    subBrandOptions: [],
    supplierOptions: [],
    category1Options: [],
    category2Options: [],
    category3Options: [],
    category4Options: [],
    usersOptions: [],
    productTypeOptions: [],
  });
  const {
    seasonOptions,
    brandOptions,
    subBrandOptions,
    supplierOptions,
    category1Options,
    category2Options,
    category3Options,
    category4Options,
    usersOptions,
    downloadOptions,
    yearOptions,
    monthOptions,
    toOptions,
    productTypeOptions,
  } = state;
  const [isApply, setIsApply] = useState(false);
  const [subBrandAccess, setSubBrandAccess] = useState(false);
  const [catBasedOnPTData, setCatBasedOnPTData] = useState(false);
  const [licensorAccess, setLicensorAccess] = useState(false);
  // Prevent re-entrant fetch loops
  const isFetchingCatsRef = useRef(false);

  useEffect(() => {
    const fetchSubBrandAccess = async () => {
      const subBrandAccessRes = await dispatch(systemMasterAction.getSubBrandAccess());
      setSubBrandAccess(subBrandAccessRes?.data || false);
    };
    fetchSubBrandAccess();
  }, [dispatch]);

  useEffect(() => {
    if (!userId) return;
    const fetchLicensorSetup = async () => {
      const res = await dispatch(systemMasterAction.getLicensorAccess()).catch(() => null);
      const hasAccess = !!res?.isActiveLicensor;
      setLicensorAccess(hasAccess);
      if (hasAccess) {
        dispatch(systemMasterAction.getLicensorDetails({ loggedInUserId: userId, isActive: true }));
      }
    };
    fetchLicensorSetup();
  }, [dispatch, userId]);

  useEffect(() => {
    if (!userId) return;
    const saved = localStorage.getItem('submitTodoListFilter');
    if (!saved) return;
    try {
      const parsed = JSON.parse(saved);
      if (parsed?.selectedLicensor?.length > 0 || parsed?.selectedLicense?.length > 0 || parsed?.selectedLikeness?.length > 0) {
        dispatch(
          todoAction.filterSortData({
            selectedLicensor: parsed.selectedLicensor || [],
            selectedLicense: parsed.selectedLicense || [],
            selectedLikeness: parsed.selectedLikeness || [],
          }),
        );
      }
      if (parsed?.selectedLicensor?.length > 0) {
        dispatch(
          systemMasterAction.getLicenseDetails({
            licensorIds: parsed.selectedLicensor,
            loggedInUserId: userId,
            isActive: true,
          }),
        );
      }
      if (parsed?.selectedLicensor?.length > 0 && parsed?.selectedLicense?.length > 0) {
        dispatch(
          systemMasterAction.getLikenessDetails({
            licensorIds: parsed.selectedLicensor,
            licenseIds: parsed.selectedLicense,
            loggedInUserId: userId,
            isActive: true,
          }),
        );
      }
    } catch (e) {
      /* ignore */
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, userId]);

  // Fetch feature flag for cascading by Product Type
  useEffect(() => {
    const fetchCatBasedOnPTData = async () => {
      try {
        const res = await dispatch(systemMasterAction.getCatBasedOnPT());
        setCatBasedOnPTData(!!res);
      } catch (e) {
        setCatBasedOnPTData(false);
      }
    };
    fetchCatBasedOnPTData();
  }, [dispatch]);

  // Ensure Product Types (Tier1) are available for the Product Type MultiSelect
  useEffect(() => {
    dispatch(
      systemMasterAction.getPCTypesDetails(
        {
          loggedInUserId: userId,
          isActive: true,
          tier: 'Tier1',
        },
        true,
      ),
    );
  }, [dispatch, userId]);

  // Build product type options locally from store list
  useEffect(() => {
    if (Array.isArray(pcTypeList) && pcTypeList.length > 0) {
      setState((prev) => ({ ...prev, productTypeOptions: createOptions(pcTypeList, 'name', '_id') }));
    } else {
      setState((prev) => ({ ...prev, productTypeOptions: [] }));
    }
  }, [pcTypeList]);

  // Helpers for cascading filtering (same as reference file)
  const getIds = (arr) => (Array.isArray(arr) ? arr.filter(Boolean) : []);
  const selectedPTIds = getIds(selected_ProductType);
  const selectedCat1Ids = getIds(selected_Category1);
  const selectedCat2Ids = getIds(selected_Category2);
  const selectedCat3Ids = getIds(selected_Category3);

  // Stable keys to avoid effect loops due to referential changes
  const selCat1Key = useMemo(() => (selectedCat1Ids.length ? selectedCat1Ids.slice().sort().join(',') : ''), [selectedCat1Ids]);
  const selCat2Key = useMemo(() => (selectedCat2Ids.length ? selectedCat2Ids.slice().sort().join(',') : ''), [selectedCat2Ids]);
  const selCat3Key = useMemo(() => (selectedCat3Ids.length ? selectedCat3Ids.slice().sort().join(',') : ''), [selectedCat3Ids]);

  // Equality helpers to avoid redundant setState
  const optionIds = (arr) =>
    Array.isArray(arr)
      ? arr
          .map((o) => String(o?._id ?? o?.value))
          .filter(Boolean)
          .sort()
      : [];
  const areOptionIdsEqual = (a, b) => {
    const aa = optionIds(a);
    const bb = optionIds(b);
    if (aa.length !== bb.length) return false;
    for (let i = 0; i < aa.length; i += 1) if (aa[i] !== bb[i]) return false;
    return true;
  };

  // Client-side filtering using useMemo (same approach as reference file)
  const category1Filtered = useMemo(() => {
    if (!catBasedOnPTData) return category1Options;
    if (!selectedPTIds?.length) return category1Options;
    const base = category1Options || [];
    const filtered = base.filter((opt) => {
      const used = Array.isArray(opt?.isUsedProductType) ? opt.isUsedProductType : [];
      const pts = Array.isArray(opt?.productType) ? opt.productType.map((p) => (typeof p === 'object' ? p?._id : p)).filter(Boolean) : [];
      return used?.some((id) => selectedPTIds.includes(id)) || pts?.some((id) => selectedPTIds.includes(id));
    });
    return filtered?.length ? filtered : base;
  }, [catBasedOnPTData, category1Options, selectedPTIds]);

  const category2Filtered = useMemo(() => {
    if (!catBasedOnPTData) return category2Options;
    if (!selectedCat1Ids?.length) return category2Options;
    const base = category2Options || [];
    const hasMeta = base.some((opt) => Array.isArray(opt?.isUsedCat1) && opt.isUsedCat1.length);
    if (!hasMeta) return base;
    const filtered = base.filter((opt) => {
      const usedCat1 = Array.isArray(opt?.isUsedCat1) ? opt.isUsedCat1 : [];
      return usedCat1?.some((id) => selectedCat1Ids.includes(id));
    });
    return filtered?.length ? filtered : base;
  }, [catBasedOnPTData, category2Options, selectedCat1Ids]);

  const category3Filtered = useMemo(() => {
    if (!catBasedOnPTData) return category3Options;
    if (!selectedCat1Ids?.length || !selectedCat2Ids?.length) return category3Options;
    const base = category3Options || [];
    const hasMeta = base.some((opt) => opt?.categoryOneId && opt?.categoryTwoId);
    if (!hasMeta) return base;
    const selC1 = selectedCat1Ids.map((id) => String(id));
    const selC2 = selectedCat2Ids.map((id) => String(id));
    const filtered = base.filter((opt) => selC1.includes(String(opt?.categoryOneId)) && selC2.includes(String(opt?.categoryTwoId)));
    return filtered?.length ? filtered : base;
  }, [catBasedOnPTData, category3Options, selectedCat1Ids, selectedCat2Ids]);

  const category4Filtered = useMemo(() => {
    if (!catBasedOnPTData) return category4Options;
    if (!selectedCat1Ids?.length || !selectedCat2Ids?.length || !selectedCat3Ids?.length) return category4Options;
    const base = category4Options || [];
    const hasMeta = base.some((opt) => opt?.categoryOneId && opt?.categoryTwoId && opt?.categoryThreeId);
    if (!hasMeta) return base;
    const selC1 = selectedCat1Ids.map((id) => String(id));
    const selC2 = selectedCat2Ids.map((id) => String(id));
    const selC3 = selectedCat3Ids.map((id) => String(id));
    const filtered = base.filter(
      (opt) => selC1.includes(String(opt?.categoryOneId)) && selC2.includes(String(opt?.categoryTwoId)) && selC3.includes(String(opt?.categoryThreeId)),
    );
    return filtered?.length ? filtered : base;
  }, [catBasedOnPTData, category4Options, selectedCat1Ids, selectedCat2Ids, selectedCat3Ids]);

  useEffect(() => {
    const isTrue = JSON.parse(localStorage.getItem('submitTodoListFilter'));
    if (isTrue) {
      for (const keys in isTrue) {
        if (isTrue[keys].length > 0) {
          setIsApply(true);
        }
      }
    }
  }, []);

  // Fetch SubBrand list when Brand changes
  useEffect(() => {
    const brandId = selected_Brand;
    if (brandId) {
      dispatch(
        systemMasterAction.getMultiSubBrandDetails({
          brandId: Array.isArray(brandId) ? brandId : [brandId],
        }),
      );
    }
  }, [dispatch, selected_Brand]);

  // Map reducer subBrand list to dropdown options
  useEffect(() => {
    if (Array.isArray(multiSubBrandList) && multiSubBrandList.length > 0) {
      const opts = createOptions(multiSubBrandList, 'name', '_id');
      setState((prev) => ({ ...prev, subBrandOptions: opts }));
    } else {
      setState((prev) => ({ ...prev, subBrandOptions: [] }));
    }
  }, [multiSubBrandList]);
  const handleSelectChange = async (value, name) => {
    const obj = {};
    set(obj, `${name}`, value);

    // When Category 1 changes, reset Category 2 and clear downstream options
    if (name === 'selected_Category1') {
      obj.selected_Category2 = [];
      setState((prev) => ({ ...prev, category3Options: [], category4Options: [] }));
    }

    // When Product Type changes and cascading is ON, clear all downstream category selections
    if (name === 'selected_ProductType' && catBasedOnPTData) {
      obj.selected_Category1 = [];
      obj.selected_Category2 = [];
      obj.selected_Category3 = [];
      obj.selected_Category4 = [];
    }

    if (
      ['selected_Category1', 'selected_Category2'].some(function (v) {
        return name.indexOf(v) >= 0;
      })
    ) {
      obj.selected_Category3 = [];
      obj.selected_Category4 = [];
    }
    if (
      ['selected_Category3'].some(function (v) {
        return name.indexOf(v) >= 0;
      })
    ) {
      obj.selected_Category4 = [];
    }
    if (name === 'selected_Supplier') {
      if (value.length === 0) {
        obj.selected_Send_To = [];
        obj.selected_DownloadBy = [];
      }
    }
    if (
      ['selected_Brand'].some(function (v) {
        return name.indexOf(v) >= 0;
      })
    ) {
      obj.selected_SubBrand = [];
      setState((prev) => ({ ...prev, subBrandOptions: [] }));
    }
    await dispatch(todoAction.filterSortData(obj));
  };

  const licensorOptions = useMemo(() => (checkArray(licensorList) ? createOptions(licensorList, 'name', '_id') : []), [licensorList]);
  const licenseOptions = useMemo(() => (checkArray(licenseList) ? createOptions(licenseList, 'name', '_id') : []), [licenseList]);
  const likenessOptions = useMemo(() => (checkArray(likenessList) ? createOptions(likenessList, 'name', '_id') : []), [likenessList]);

  const handleFilterLicense = (val, key) => {
    const newData = { [key]: val };
    if (key === 'selectedLicensor') {
      newData.selectedLicense = [];
      newData.selectedLikeness = [];
      dispatch(systemMasterAction.clearLikenessData());
      if (val.length > 0) {
        dispatch(systemMasterAction.getLicenseDetails({ licensorIds: val, loggedInUserId: userId, isActive: true }));
      } else {
        dispatch(systemMasterAction.clearLicenseData());
      }
    }
    if (key === 'selectedLicense') {
      newData.selectedLikeness = [];
      dispatch(systemMasterAction.clearLikenessData());
      if (val.length > 0 && checkArray(selectedLicensor)) {
        dispatch(systemMasterAction.getLikenessDetails({ licensorIds: selectedLicensor, licenseIds: val, loggedInUserId: userId, isActive: true }));
      }
    }
    dispatch(todoAction.filterSortData(newData));
  };

  const clearAll = async () => {
    const paramsObj = {
      seasons: [],
      year: [],
      brand: [],
      subBrand: [],
      supplier: [],
      colour: [],
      category1: [],
      category2: [],
      category3: [],
      category4: [],
      productTypeId: [],
      licensorId: [],
      licenseId: [],
      likenessId: [],
      status: {},
      pageIndex: 1,
      pageSize: Page_Size,
      type: 'submit-download',
    };
    const sortinObj = {
      selected_Season: [],
      selected_Brand: [],
      selected_SubBrand: [],
      selected_Supplier: [],
      selected_Category1: [],
      selected_Category2: [],
      selected_Category3: [],
      selected_Category4: [],
      selected_ProductType: [],
      selected_CreatedBy: [],
      selected_ModifiedBy: [],
      selected_Status: [],
      selected_Send_To: [],
      selected_SendBy: [],
      selected_Submitted: [],
      selected_DownloadBy: [],
      selected_Month: [],
      selected_Year: [],
      selectedLicensor: [],
      selectedLicense: [],
      selectedLikeness: [],
      loading: true,
      sortOrder,
      sortPropertyName,
      pageIndex: 1,
      pageSize: Page_Size,
    };
    dispatch(systemMasterAction.clearLicenseData());
    dispatch(systemMasterAction.clearLikenessData());
    localStorage.removeItem('submitTodoListFilter');

    const res = await dispatch(todoAction.getSubmitList(paramsObj));
    setIsApply(false);
    if (res) await dispatch(todoAction.filterSortData(sortinObj));
  };

  const applyFilter = async () => {
    setIsApply(true);
    const paramsObj = {
      month: selected_Month,
      year: selected_Year,
      seasons: selected_Season,
      brand: selected_Brand,
      subBrand: selected_SubBrand,
      supplier: selected_Supplier,
      category1: selected_Category1,
      category2: selected_Category2,
      category3: selected_Category3,
      category4: selected_Category4,
      productTypeId: Array.isArray(selected_ProductType) ? selected_ProductType : [],
      createdBy: selected_CreatedBy,
      modifiedBy: selected_ModifiedBy,
      sendTo: selected_Send_To,
      downloadBy: selected_DownloadBy,
      sendBy: selected_SendBy,
      sendBy1: selected_Submitted,
      licensorId: selectedLicensor,
      licenseId: selectedLicense,
      likenessId: selectedLikeness,
      loading: true,
      sortOrder,
      sortPropertyName,
      pageIndex: 1,
      pageSize: Page_Size,
      type: 'submit-download',
    };

    const paramsObj1 = {
      selected_Season,
      selected_Brand,
      selected_SubBrand,
      selected_Supplier,
      selected_Category1,
      selected_Category2,
      selected_Category3,
      selected_Category4,
      selected_ProductType,
      selected_CreatedBy,
      selected_ModifiedBy,
      selected_Send_To,
      selected_Submitted,
      selected_DownloadBy,
      selected_SendBy,
      selected_Month,
      selected_Year,
      selectedLicensor,
      selectedLicense,
      selectedLikeness,
    };

    localStorage.setItem('submitTodoListFilter', JSON.stringify(paramsObj1));
    await dispatch(todoAction.getSubmitList(paramsObj));
    const obj = {
      pageIndex: 1,
      pageSize: Page_Size,
    };
    await dispatch(todoAction.filterSortData(obj));
  };

  const fetchCategories = useCallback(async () => {
    // Hydrate Category 1 and 2 base lists only
    for (const cat of [1, 2]) {
      const res = await dispatch(
        commonAction.fetchCategoryAction({
          accessedFrom: 'masters',
          categoryType: cat,
          loggedInUserId: userId,
        }),
      );
      setState((prevState) => ({ ...prevState, [`category${cat}Options`]: createOptions(res, 'name', '_id') }));
    }

    // Hydrate Category 3 if Cat1+Cat2 are already selected (support multi Cat1/Cat2)
    if (Array.isArray(selected_Category1) && selected_Category1.length && Array.isArray(selected_Category2) && selected_Category2.length) {
      const c1s = selected_Category1;
      const c2s = selected_Category2;
      const calls3 = [];
      c1s.forEach((c1) => {
        c2s.forEach((c2) => {
          calls3.push(
            dispatch(
              commonAction.fetchCategoryAction({
                accessedFrom: 'masters',
                categoryType: 3,
                loggedInUserId: userId,
                categoryOneId: c1,
                categoryTwoId: c2,
                isActive: 1,
              }),
            ),
          );
        });
      });
      const results3 = await Promise.all(calls3);
      const merged3 = [];
      const seen3 = new Set();
      results3.forEach((res) => {
        const arr = Array.isArray(res?.data) ? res.data : res;
        if (Array.isArray(arr)) {
          arr.forEach((item) => {
            const key = String(item?._id ?? item?.value ?? '');
            if (key && !seen3.has(key)) {
              seen3.add(key);
              merged3.push(item);
            }
          });
        }
      });
      setState((prevState) => ({ ...prevState, category3Options: createOptions(merged3, 'name', '_id') }));
    }

    // Hydrate Category 4 if Cat1+Cat2+Cat3 are already selected (support multi Cat1/Cat2/Cat3)
    if (
      Array.isArray(selected_Category1) &&
      selected_Category1.length &&
      Array.isArray(selected_Category2) &&
      selected_Category2.length &&
      Array.isArray(selected_Category3) &&
      selected_Category3.length
    ) {
      const c1s = selected_Category1;
      const c2s = selected_Category2;
      const c3s = selected_Category3;
      const calls4 = [];
      c1s.forEach((c1) => {
        c2s.forEach((c2) => {
          c3s.forEach((c3) => {
            calls4.push(
              dispatch(
                commonAction.fetchCategoryAction({
                  accessedFrom: 'masters',
                  categoryType: 4,
                  loggedInUserId: userId,
                  categoryOneId: c1,
                  categoryTwoId: c2,
                  categoryThreeId: c3,
                  isActive: 1,
                }),
              ),
            );
          });
        });
      });
      const results4 = await Promise.all(calls4);
      const merged4 = [];
      const seen4 = new Set();
      results4.forEach((res) => {
        const arr = Array.isArray(res?.data) ? res.data : res;
        if (Array.isArray(arr)) {
          arr.forEach((item) => {
            const key = String(item?._id ?? item?.value ?? '');
            if (key && !seen4.has(key)) {
              seen4.add(key);
              merged4.push(item);
            }
          });
        }
      });
      setState((prevState) => ({ ...prevState, category4Options: createOptions(merged4, 'name', '_id') }));
    }
  }, [dispatch, userId, selected_Category1, selected_Category2, selected_Category3]);

  // all useEffect
  // call all api's for filter section to fill the dropdown list
  useEffect(() => {
    const callFn = async () => {
      let downloadbyOptions = [];
      let supplier_Options = [];
      let brand_Options = [];
      let season_Options = [];
      let user_options = [];
      const BrandRes = isSupplierLoggedIn
        ? await dispatch(getSupplierBrandsAction(userId))
        : await dispatch(
            commonAction.fetchAllBrandAction({
              loggedInUserId: userId,
            }),
          );

      await fetchCategories();
      const sesonres = await dispatch(commonAction.getSeasonsAction({ loggedInUserId: userId }));

      if (!isSupplierLoggedIn) {
        const user_list = await dispatch(
          commonAction.getCompanyUsers({
            CompanyUsers: true,
          }),
        );
        const loggedInUserInfo = JSON.parse(localStorage.getItem('loggedInUserInfo'));

        loggedInUserInfo._id = loggedInUserInfo?.userId;
        if (checkArray(user_list)) {
          user_options = createOptions([loggedInUserInfo, ...user_list], 'firstName', '_id');
          if (user_options && user_options.length > 0) {
            user_options = user_options.map((userdata) => {
              userdata.label = `${userdata?.firstName} ${userdata?.lastName}`;
              return userdata;
            });
          }
          downloadbyOptions = [...downloadbyOptions, ...user_options];
        }
      }

      if (checkArray(sesonres)) {
        season_Options = createOptions(sesonres, 'name', '_id');
      }
      if (checkArray(BrandRes)) {
        brand_Options = createOptions(BrandRes, 'name', '_id');
      }

      if (!isSupplierLoggedIn) {
        let supp_list = [];

        if (isCompanyUserLoggedIn) {
          supp_list = await dispatch(commonAction.getUserSuppliers());

          supp_list = supp_list.suppliers;
          supp_list = supp_list.length
            ? supp_list.map((ele) => {
                if (ele.companies.length) {
                  ele.name = ele.companies[0].supplierName;
                }
                return ele;
              })
            : [];
        } else {
          supp_list = await dispatch(
            commonAction.getSuppliers({
              loggedInUserId: userId,
            }),
          );
        }
        if (checkArray(supp_list)) {
          supplier_Options = createOptions(supp_list, 'name', '_id');
          downloadbyOptions = [...downloadbyOptions, ...supplier_Options];
        }
      }

      setState((prevState) => ({
        ...prevState,
        toOptions: downloadbyOptions,
        downloadOptions: downloadbyOptions,
        supplierOptions: supplier_Options,
        brandOptions: brand_Options,
        seasonOptions: season_Options,
        usersOptions: user_options,
      }));
    };
    if (!isEmpty(userId)) callFn();
  }, [dispatch, userId]); // eslint-disable-line

  useEffect(() => {
    const callFn = async () => {
      if (isFetchingCatsRef.current) return;
      isFetchingCatsRef.current = true;
      try {
        const c1s = selectedCat1Ids;
        const c2s = selectedCat2Ids;
        const c3s = selectedCat3Ids;
        if (c1s.length && c2s.length && c3s.length && !isEmpty(userId)) {
          // Fetch Category 4 for every C1×C2×C3 combination and merge
          const calls = [];
          c1s.forEach((c1) => {
            c2s.forEach((c2) => {
              c3s.forEach((id) => {
                calls.push(
                  dispatch(
                    commonAction.fetchCategoryAction({
                      accessedFrom: 'masters',
                      categoryType: 4,
                      loggedInUserId: userId,
                      categoryOneId: c1,
                      categoryTwoId: c2,
                      categoryThreeId: id,
                      isActive: 1,
                    }),
                  ),
                );
              });
            });
          });
          const results = await Promise.all(calls);
          const merged = [];
          const seen = new Set();
          results.forEach((res) => {
            const arr = Array.isArray(res?.data) ? res.data : res;
            if (Array.isArray(arr)) {
              arr.forEach((item) => {
                const key = String(item?._id ?? item?.value ?? '');
                if (key && !seen.has(key)) {
                  seen.add(key);
                  merged.push(item);
                }
              });
            }
          });
          const next = createOptions(merged, 'name', '_id');
          if (!areOptionIdsEqual(category4Options, next)) {
            setState((prevState) => ({ ...prevState, category4Options: next }));
          }

          // Also hydrate Category 3 options if empty
          if (!Array.isArray(category3Options) || category3Options.length === 0) {
            const calls3 = [];
            c1s.forEach((c1) => {
              c2s.forEach((c2) => {
                calls3.push(
                  dispatch(
                    commonAction.fetchCategoryAction({
                      accessedFrom: 'masters',
                      categoryType: 3,
                      loggedInUserId: userId,
                      categoryOneId: c1,
                      categoryTwoId: c2,
                      isActive: 1,
                    }),
                  ),
                );
              });
            });
            const results3 = await Promise.all(calls3);
            const merged3 = [];
            const seen3 = new Set();
            results3.forEach((res) => {
              const arr = Array.isArray(res?.data) ? res.data : res;
              if (Array.isArray(arr)) {
                arr.forEach((item) => {
                  const key = String(item?._id ?? item?.value ?? '');
                  if (key && !seen3.has(key)) {
                    seen3.add(key);
                    merged3.push(item);
                  }
                });
              }
            });
            const next3 = createOptions(merged3, 'name', '_id');
            if (!areOptionIdsEqual(category3Options, next3)) {
              setState((prevState) => ({ ...prevState, category3Options: next3 }));
            }
          }
        } else if (c1s.length && c2s.length && !isEmpty(userId)) {
          // Fetch Category 3 for every C1×C2 combination and merge
          const calls = [];
          c1s.forEach((c1) => {
            c2s.forEach((c2) => {
              calls.push(
                dispatch(
                  commonAction.fetchCategoryAction({
                    accessedFrom: 'masters',
                    categoryType: 3,
                    loggedInUserId: userId,
                    categoryOneId: c1,
                    categoryTwoId: c2,
                    isActive: 1,
                  }),
                ),
              );
            });
          });
          const results = await Promise.all(calls);
          const merged = [];
          const seen = new Set();
          results.forEach((res) => {
            const arr = Array.isArray(res?.data) ? res.data : res;
            if (Array.isArray(arr)) {
              arr.forEach((item) => {
                const key = String(item?._id ?? item?.value ?? '');
                if (key && !seen.has(key)) {
                  seen.add(key);
                  merged.push(item);
                }
              });
            }
          });
          const next = createOptions(merged, 'name', '_id');
          if (!areOptionIdsEqual(category3Options, next)) {
            setState((prevState) => ({ ...prevState, category3Options: next }));
          }
        }
      } finally {
        isFetchingCatsRef.current = false;
      }
    };
    callFn();
  }, [selCat1Key, selCat2Key, selCat3Key, dispatch, userId]);

  return (
    <button
      className={isApply ? 'filter-btn filter-button_delete active ' : 'filter-btn filter-button_delete '}
      type="button"
      title={t('TAKING_DATES.SUBMIT_FILTER')}
      aria-label="text"
    >
      <OverlayTrigger
        placement={t('TABLE_HEADERS_LIST.BUTTON')}
        trigger="click"
        rootClose
        overlay={
          <Popover className="custom-popover horizontal-view">
            <Popover.Content>
              <div className="product-costing-filter">
                <div className="filter-text">
                  <FormGroup className="mb-2">
                    <div className="custom-react-select ">
                      <MultiSelect
                        hasSelectAll
                        name="brand"
                        options={brandOptions}
                        className="product-custom-multiselect common-new-multiselect w-100"
                        value={brandOptions.filter((val) => !isEmpty(selected_Brand) && selected_Brand.includes(val._id))}
                        overrideStrings={{
                          selectSomeItems: t('COSTING_TO_DO_LIST.SELECT_BRAND'),
                        }}
                        labelledBy={t('COSTING_TO_DO_LIST.SELECT_BRAND')}
                        onChange={(selected) =>
                          handleSelectChange(
                            selected.map((val) => val._id),
                            'selected_Brand',
                          )
                        }
                      />
                    </div>
                  </FormGroup>
                  {subBrandAccess && (
                    <FormGroup className="mb-2">
                      <div className="custom-react-select ">
                        <MultiSelect
                          hasSelectAll
                          name="subBrand"
                          options={subBrandOptions}
                          className="product-custom-multiselect common-new-multiselect w-100"
                          value={subBrandOptions.filter((val) => !isEmpty(selected_SubBrand) && selected_SubBrand.includes(val._id))}
                          overrideStrings={{
                            selectSomeItems: tReports('TODO_COMMON.SELECT_SUBBRAND'),
                          }}
                          labelledBy={tReports('TODO_COMMON.SELECT_SUBBRAND')}
                          onChange={(selected) =>
                            handleSelectChange(
                              selected.map((val) => val._id),
                              'selected_SubBrand',
                            )
                          }
                        />
                      </div>
                    </FormGroup>
                  )}
                  {catBasedOnPTData && (
                    <FormGroup className="mb-2">
                      <div className="custom-react-select ">
                        <MultiSelect
                          hasSelectAll
                          name="productType"
                          options={productTypeOptions}
                          className="product-custom-multiselect common-new-multiselect w-100"
                          value={productTypeOptions.filter((opt) => Array.isArray(selected_ProductType) && selected_ProductType.includes(opt._id))}
                          overrideStrings={{
                            selectSomeItems: t('COSTING_TO_DO_LIST.SELECT_CATEGORY_1'),
                          }}
                          labelledBy={t('COSTING_TO_DO_LIST.SELECT_CATEGORY_1')}
                          onChange={(selected) =>
                            handleSelectChange(Array.isArray(selected) ? selected.map((opt) => opt?._id).filter(Boolean) : [], 'selected_ProductType')
                          }
                        />
                      </div>
                    </FormGroup>
                  )}
                  <FormGroup className="mb-2">
                    <div className="custom-react-select ">
                      <MultiSelect
                        hasSelectAll
                        name="category1"
                        options={catBasedOnPTData ? category1Filtered : category1Options}
                        className="product-custom-multiselect common-new-multiselect w-100"
                        value={(catBasedOnPTData ? category1Filtered : category1Options).filter(
                          (val) => !isEmpty(selected_Category1) && selected_Category1.includes(val._id),
                        )}
                        overrideStrings={{
                          selectSomeItems: t('COSTING_TO_DO_LIST.SELECT_CATEGORY_1'),
                        }}
                        labelledBy={t('COSTING_TO_DO_LIST.SELECT_CATEGORY_1')}
                        onChange={(selected) =>
                          handleSelectChange(
                            selected.map((val) => val._id),
                            'selected_Category1',
                          )
                        }
                      />
                    </div>
                  </FormGroup>
                  <FormGroup className="mb-2">
                    <div className="custom-react-select ">
                      <MultiSelect
                        hasSelectAll
                        name="category2"
                        options={catBasedOnPTData ? category2Filtered : category2Options}
                        className="product-custom-multiselect common-new-multiselect w-100"
                        value={(catBasedOnPTData ? category2Filtered : category2Options)?.filter(
                          (val) => !isEmpty(selected_Category2) && selected_Category2?.includes(val?._id),
                        )}
                        overrideStrings={{
                          selectSomeItems: t('COSTING_TO_DO_LIST.SELECT_CATEGORY_2'),
                        }}
                        labelledBy={t('COSTING_TO_DO_LIST.SELECT_CATEGORY_2')}
                        onChange={(selected) =>
                          handleSelectChange(
                            selected.map((val) => val._id),
                            'selected_Category2',
                          )
                        }
                      />
                    </div>
                  </FormGroup>
                  <FormGroup className="mb-2">
                    <div className="custom-react-select ">
                      <MultiSelect
                        hasSelectAll
                        name="category3"
                        options={catBasedOnPTData ? category3Filtered : category3Options}
                        className="product-custom-multiselect common-new-multiselect w-100"
                        value={(catBasedOnPTData ? category3Filtered : category3Options).filter(
                          (val) => !isEmpty(selected_Category3) && selected_Category3.includes(val._id),
                        )}
                        overrideStrings={{
                          selectSomeItems: t('SPEC_TEMP_LIBRARY.CATEGORY3_PLACEHOLDER'),
                        }}
                        labelledBy={t('SPEC_TEMP_LIBRARY.CATEGORY3_PLACEHOLDER')}
                        onChange={(selected) =>
                          handleSelectChange(
                            selected.map((val) => val._id),
                            'selected_Category3',
                          )
                        }
                      />
                    </div>
                  </FormGroup>
                  <FormGroup className="mb-2">
                    <div className="custom-react-select ">
                      <MultiSelect
                        hasSelectAll
                        name="category4"
                        options={catBasedOnPTData ? category4Filtered : category4Options}
                        className="product-custom-multiselect common-new-multiselect w-100"
                        value={(catBasedOnPTData ? category4Filtered : category4Options).filter(
                          (val) => !isEmpty(selected_Category4) && selected_Category4.includes(val._id),
                        )}
                        overrideStrings={{
                          selectSomeItems: t('COSTING_TO_DO_LIST.SELECT_CATEGORY_4'),
                        }}
                        labelledBy={t('COSTING_TO_DO_LIST.SELECT_CATEGORY_4')}
                        onChange={(selected) =>
                          handleSelectChange(
                            selected.map((val) => val._id),
                            'selected_Category4',
                          )
                        }
                      />
                    </div>
                  </FormGroup>
                  <FormGroup className="mb-2">
                    <div className="custom-react-select ">
                      <MultiSelect
                        hasSelectAll
                        name="season"
                        options={seasonOptions}
                        className="product-custom-multiselect common-new-multiselect w-100"
                        value={seasonOptions.filter((val) => !isEmpty(selected_Season) && selected_Season.includes(val._id))}
                        overrideStrings={{
                          selectSomeItems: t('COSTING_TO_DO_LIST.SELECT_SEASON'),
                        }}
                        labelledBy={t('COSTING_TO_DO_LIST.SELECT_SEASON')}
                        onChange={(selected) =>
                          handleSelectChange(
                            selected.map((val) => val._id),
                            'selected_Season',
                          )
                        }
                      />
                    </div>
                  </FormGroup>
                  <FormGroup className="mb-2">
                    <div className="custom-react-select ">
                      <MultiSelect
                        hasSelectAll
                        name="year"
                        options={monthOptions}
                        className="product-custom-multiselect common-new-multiselect w-100"
                        value={monthOptions.filter((val) => !isEmpty(selected_Month) && selected_Month?.includes(val._id))}
                        overrideStrings={{
                          selectSomeItems: t('PROGRESS_INDICATOR.SELECT_MONTH'),
                        }}
                        labelledBy={t('PROGRESS_INDICATOR.SELECT_MONTH')}
                        onChange={(selected) =>
                          handleSelectChange(
                            selected.map((val) => val?._id),
                            'selected_Month',
                          )
                        }
                      />
                    </div>
                  </FormGroup>
                  <FormGroup className="mb-2">
                    <div className="custom-react-select ">
                      <MultiSelect
                        hasSelectAll
                        name="season"
                        ItemRenderer={CurrentYearItemRenderer}
                        options={yearOptions}
                        className="product-custom-multiselect common-new-multiselect w-100"
                        value={yearOptions.filter((val) => !isEmpty(selected_Year) && selected_Year?.includes(val?._id))}
                        overrideStrings={{
                          selectSomeItems: t('COSTING_TO_DO_LIST.SELECT_YEAR'),
                        }}
                        labelledBy={t('COSTING_TO_DO_LIST.SELECT_YEAR')}
                        onChange={(selected) =>
                          handleSelectChange(
                            selected.map((val) => val._id),
                            'selected_Year',
                          )
                        }
                      />
                    </div>
                  </FormGroup>

                  {!isSupplierLoggedIn && (
                    <FormGroup className="mb-2">
                      <div className="custom-react-select ">
                        <MultiSelect
                          hasSelectAll
                          name="supplier"
                          options={supplierOptions}
                          className="product-custom-multiselect common-new-multiselect w-100"
                          value={supplierOptions.filter((val) => !isEmpty(selected_Supplier) && selected_Supplier.includes(val._id))}
                          overrideStrings={{
                            selectSomeItems: t('PURCHASE_ORDER_NUMBER.SELECT'),
                          }}
                          labelledBy={t('PURCHASE_ORDER_NUMBER.SELECT')}
                          onChange={(selected) =>
                            handleSelectChange(
                              selected.map((val) => val._id),
                              'selected_Supplier',
                            )
                          }
                        />
                      </div>
                    </FormGroup>
                  )}
                  <FormGroup className="mb-2">
                    <div className="custom-react-select ">
                      <MultiSelect
                        hasSelectAll
                        name="supplier"
                        options={usersOptions}
                        className="product-custom-multiselect common-new-multiselect w-100"
                        value={usersOptions.filter((val) => !isEmpty(selected_SendBy) && selected_SendBy.includes(val._id))}
                        overrideStrings={{
                          selectSomeItems: t('COSTING_TO_DO_LIST.SELECT_SUPLIERS'),
                        }}
                        labelledBy={t('COSTING_TO_DO_LIST.SELECT_SUPLIERS')}
                        onChange={(selected) =>
                          handleSelectChange(
                            selected.map((val) => val._id),
                            'selected_SendBy',
                          )
                        }
                      />
                    </div>
                  </FormGroup>
                  {isAnySupplierSelected && (
                    <FormGroup className="mb-2">
                      <div className="custom-react-select ">
                        <MultiSelect
                          hasSelectAll
                          name="supplier"
                          options={toOptions}
                          className="product-custom-multiselect common-new-multiselect w-100"
                          value={toOptions.filter((val) => !isEmpty(selected_Send_To) && selected_Send_To.includes(val.email))}
                          overrideStrings={{
                            selectSomeItems: t('COSTING_TO_DO_LIST.SELECT_SUPLIERS'),
                          }}
                          labelledBy={t('COSTING_TO_DO_LIST.SELECT_SUPLIERS')}
                          onChange={(selected) =>
                            handleSelectChange(
                              selected.map((val) => val.email),
                              'selected_Send_To',
                            )
                          }
                        />
                      </div>
                    </FormGroup>
                  )}
                  {isAnySupplierSelected && (
                    <FormGroup className="mb-2">
                      <div className="custom-react-select ">
                        <MultiSelect
                          hasSelectAll
                          name="supplier"
                          options={downloadOptions}
                          className="product-custom-multiselect common-new-multiselect w-100"
                          value={downloadOptions.filter((val) => !isEmpty(selected_DownloadBy) && selected_DownloadBy.includes(val.email))}
                          overrideStrings={{
                            selectSomeItems: t('COSTING_TO_DO_LIST.SELECT_SUPLIERS'),
                          }}
                          labelledBy={t('COSTING_TO_DO_LIST.SELECT_SUPLIERS')}
                          onChange={(selected) =>
                            handleSelectChange(
                              selected.map((val) => val.email),
                              'selected_DownloadBy',
                            )
                          }
                        />
                      </div>
                    </FormGroup>
                  )}
                  {licensorAccess && (
                    <>
                      <FormGroup className="mb-2">
                        <div className="custom-react-select">
                          <MultiSelect
                            hasSelectAll
                            options={licensorOptions}
                            className="product-custom-multiselect common-new-multiselect w-100"
                            value={licensorOptions.filter((val) => checkArray(selectedLicensor) && selectedLicensor.includes(val._id))}
                            overrideStrings={{
                              selectSomeItems: tReports('LICENCES.SELECT_LICENSOR'),
                            }}
                            labelledBy={tReports('LICENCES.SELECT_LICENSOR')}
                            onChange={(selected) =>
                              handleFilterLicense(
                                selected.map((val) => val._id),
                                'selectedLicensor',
                              )
                            }
                          />
                        </div>
                      </FormGroup>
                      <FormGroup className="mb-2">
                        <div className="custom-react-select">
                          <MultiSelect
                            hasSelectAll
                            options={licenseOptions}
                            className="product-custom-multiselect common-new-multiselect w-100"
                            value={licenseOptions.filter((val) => checkArray(selectedLicense) && selectedLicense.includes(val._id))}
                            overrideStrings={{
                              selectSomeItems: tReports('LICENCES.SELECT_LICENSE'),
                            }}
                            labelledBy={tReports('LICENCES.SELECT_LICENSE')}
                            onChange={(selected) =>
                              handleFilterLicense(
                                selected.map((val) => val._id),
                                'selectedLicense',
                              )
                            }
                          />
                        </div>
                      </FormGroup>
                      <FormGroup className="mb-2">
                        <div className="custom-react-select">
                          <MultiSelect
                            hasSelectAll
                            options={likenessOptions}
                            className="product-custom-multiselect common-new-multiselect w-100"
                            value={likenessOptions.filter((val) => checkArray(selectedLikeness) && selectedLikeness.includes(val._id))}
                            overrideStrings={{
                              selectSomeItems: tReports('LICENCES.SELECT_LIKENESS'),
                            }}
                            labelledBy={tReports('LICENCES.SELECT_LIKENESS')}
                            onChange={(selected) =>
                              handleFilterLicense(
                                selected.map((val) => val._id),
                                'selectedLikeness',
                              )
                            }
                          />
                        </div>
                      </FormGroup>
                    </>
                  )}
                </div>

                <div className="d-flex justify-content-end">
                  <button type="button" aria-label="Clear" className="btn p-0 d-inline-flex" onClick={clearAll}>
                    <span className="icon-Clear-New f-24" />
                  </button>
                  <button type="button" aria-label="Apply" className="btn p-0 d-inline-flex ml-16" onClick={applyFilter}>
                    <span className="icon-Tick-1 f-28" />
                  </button>
                </div>
              </div>
            </Popover.Content>
          </Popover>
        }
      >
        <i className="icon-Filter f-26" />
      </OverlayTrigger>
    </button>
  );
}

export default SubmitFilter;
